import React, { useEffect } from "react"
import PropTypes from "prop-types"
import BigGreenCheck from "./BigGreenCheck"
import { Icon } from "source/shared/components"
import Nl2Br from "./Nl2br"
import { HeadingField } from "./field_components/basic_fields"
import { Description } from "./ui"

const Thanks = ({
  attachments,
  verificationRequired,
  sendSubmissionNotificationToSubmitter,
  submissionConfirmation,
  onClick,
}) => {
  const { person_name: personName, person_email: personEmail } =
    submissionConfirmation.basicInformation
  useEffect(() => {
    window.scrollTo(0, 0)
  })

  const submittionDetailsPresent =
    submissionConfirmation.groupedSubmissionValues.length > 0

  return (
    <>
      <div>
        {!verificationRequired && (
          <div className="alert success-alert">
            <div className="d-f p-r mb-1">
              <BigGreenCheck />
              <h1 className="h2 pl-5">
                Thanks! Your answers have been submitted.
              </h1>
            </div>
            {submittionDetailsPresent && (
              <p className="pl-5 mb-4p">
                Your submission details are below
                {sendSubmissionNotificationToSubmitter
                  ? ", and a copy has been emailed to you"
                  : ""}
                .
              </p>
            )}
          </div>
        )}

        {verificationRequired && (
          <div>
            <div className="alert warning-alert p-3">
              <div className="d-f p-r mb-2 ai-c">
                <BigGreenCheck style={{ height: 24, width: 24 }} />
                <p className="h2 fw-400 pl-5 mb-0">
                  Your form has been submitted.
                </p>
              </div>
              {sendSubmissionNotificationToSubmitter && (
                <p className="pl-5 fs-3" style={{ marginTop: "-16px" }}>
                  A copy has been emailed to you.
                </p>
              )}
              <div className="d-f p-r ai-c">
                <span className="icon c-citrine mr-1" style={{ fontSize: 24 }}>
                  <Icon symbol="general#exclamation-circle" aria-hidden />
                </span>
                <h1 className="h2 pl-1">
                  Next step: check your email to confirm your changes.
                </h1>
              </div>
              <p className="pl-5 mb-4p fs-3">
                Your changes will only take effect after confirmation.
              </p>
            </div>
          </div>
        )}
      </div>
      <div>
        <div
          className="mb-2 pb-1"
          style={{
            borderBottomWidth: "1px",
            borderBottomStyle: "solid",
            borderBottomColor: "var(--color-tint6)",
          }}
        >
          <div className="d-f fd-c my-1">
            <div>{personName}</div>
            <div>{personEmail}</div>
          </div>
        </div>
        {submittionDetailsPresent && (
          <div
            className="mb-2 pb-1 "
            style={{
              borderBottomWidth: "1px",
              borderBottomStyle: "solid",
              borderBottomColor: "var(--color-tint6)",
            }}
          >
            <h2 className="h1">Form answers</h2>
            {submissionConfirmation.groupedSubmissionValues.map((group) => {
              const fileField = attachments.find(
                (att) =>
                  parseInt(att.form_field_id, 10) === parseInt(group.id, 10),
              )
              const isTextField =
                group.field_type === "text" ||
                group.field_type === "note" ||
                group.custom_field_data_type === "text"

              return (
                <div key={`group_${group.id}`} className="my-3">
                  {group.field_type === "heading" ? (
                    <HeadingField
                      key={group.id}
                      id={group.id}
                      attributes={group}
                    />
                  ) : (
                    <div className="d-f fd-c">
                      <div className="label pb-2p">{group.label}</div>
                      {group.description && (
                        <div style={{ marginBottom: -10 }}>
                          <Description description={group.description} />
                        </div>
                      )}
                    </div>
                  )}
                  {group.values.map((val) => {
                    if (fileField) {
                      const previews = fileField.files.map((f) => {
                        return {
                          url: window.URL.createObjectURL(f),
                          name: f.name,
                          extension: f.name.split(".").pop(),
                        }
                      })

                      return previews.map((f) => (
                        <div key={f.url} className="my-1">
                          <a
                            href={f.url}
                            target="_blank"
                            className="d-f ai-c"
                            rel="noreferrer"
                          >
                            <div
                              className="mr-1 lh-1 d-f ai-c jc-c c-tint4 o-h br-4p"
                              style={{
                                width: 28,
                                height: 28,
                                flex: "0 0 28px",
                                border: "1px solid",
                              }}
                            >
                              {["jpg", "jpeg", "gif", "png"].includes(
                                f.extension.toLowerCase(),
                              ) ? (
                                <img
                                  width={28}
                                  height={28}
                                  style={{ objectFit: "cover" }}
                                  src={f.url}
                                  alt=""
                                />
                              ) : (
                                <div className="fs-4 c-tint3 mt-2p">
                                  <Icon symbol="general#outlined-generic-file" />
                                </div>
                              )}
                            </div>
                            <span className="overflow-text">{f.name}</span>
                            <span className="ml-1 fs-4 mt-4p">
                              <Icon symbol="general#new-window" />
                            </span>
                          </a>
                        </div>
                      ))
                    } else if (isTextField) {
                      return (
                        <div key={`${group.id}_${val}`}>
                          <Nl2Br>{val}</Nl2Br>
                        </div>
                      )
                    } else if (val) {
                      return <div key={`${group.id}_${val}`}>{val}</div>
                    } else {
                      return <br key={`${group.id}_${val}_br`} />
                    }
                  })}
                </div>
              )
            })}
          </div>
        )}
      </div>
      <div className="my-4">
        <button type="button" className="btn link-btn" onClick={onClick}>
          Submit another response
        </button>
      </div>
    </>
  )
}

Thanks.propTypes = {
  attachments: PropTypes.array,
  verificationRequired: PropTypes.bool,
  sendSubmissionNotificationToSubmitter: PropTypes.bool,
  submissionConfirmation: PropTypes.shape({
    basicInformation: PropTypes.shape({
      person_name: PropTypes.string,
      person_email: PropTypes.string,
    }),
    groupedSubmissionValues: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        field_type: PropTypes.string,
        custom_field_data_type: PropTypes.string,
        label: PropTypes.string,
        description: PropTypes.string,
        values: PropTypes.array,
      }),
    ),
  }),
  onClick: PropTypes.func,
}

export default Thanks
